// Auth routes (used by both auth and admin-panel)
export const AUTH = {
    SIGN_IN: '/sign-in',
    SIGN_UP: '/sign-up',
    FORGOT_PASSWORD: '/forgot-password',
    CHANGE_PASSWORD: '/change-password',
    SETUP_PASSWORD: '/setup-password',
    TWO_FA: '/two-fa',
    VERIFY_EMAIL: '/verify',
    RESET_EMAIL: '/reset',
    AUTH_REST: '/auth/reset/',
    AUTH_ACTIVATED: '/auth/activated',
    ACCESS_REQUEST_NEEDED: '/access-request-needed',
    ERROR: '/error',
};

// Admin panel routes (used by admin-panel only)
export const ADMIN_PANEL = {
    DEFAULT: '/',
    DEFAULT_EMPTY: '*',
    ERROR: '/error',

    ORGANIZATIONS: {
        ROOT: '/organizations/',
        CREATE: '/organizations/create/',
    },
    ACCOUNTS: {
        ROOT: '/accounts/',
    },

    BILLING: {
        ROOT: '/billing',
        TOPUP: '/billing/topup',
        CRYPTO: '/billing/crypto',
    },
    SETTINGS: {
        ROOT: '/settings',
        COMPANY_SETTINGS: '/settings/company-settings',
        USER_SETTINGS: '/settings/user-settings',
        ADD_ORGANIZATION: '/settings/company-settings/add-organization',
        INVITE_TEAM_MEMBERS: '/settings/company-settings/invite-team-members',
        EDIT_ORGANIZATION: '/settings/company-settings/edit-organization',
    },
    CONTACT_US: '/contact-us',
};

// ONBOARDING and SIGN_UP_COMPANY routes (for auth)
export const ONBOARDING = '/onboarding';
export const SIGN_UP_COMPANY = {
    ROOT: '/sign-up-company',
    REGISTRATION: '/sign-up-company/registration',
    SCHEDULE: '/sign-up-company/schedule',
};

export const ROUTES = {
    // Common routes
    DEFAULT: '/',
    DEFAULT_EMPTY: '*',

    // Auth routes
    ...AUTH,

    // Admin panel routes
    ADMIN_PANEL: { ...ADMIN_PANEL },

    // Sign up company routes
    SIGN_UP_COMPANY,
    ONBOARDING,

    CONTACT_US: '/contact-us',

    // KYC routes
    KYC: {
        ROOT: '/kyc/',
        DEFAULT_EMPTY: '/kyc/*',
        DASHBOARD: '/kyc/dashboard/',
        VERIFICATION_LOG: '/kyc/verification-log',
        ONBOARDING: {
            ROOT: '/kyc/onboarding',
            POI: '/kyc/onboarding/poi',
        },
        APPLICANTS: {
            LEGACY: '/applicants/*',
            ROOT: '/kyc/applicants/',
            CREATE_APPLICANT: '/kyc/applicants/create-applicant',
            DETAILS: '/kyc/applicants/details/',
            REPORT: '/kyc/applicants/report',
            BULK_UPLOAD: '/kyc/applicants/bulk-upload',
            DOCVER_REQUEST: '/kyc/applicants/docver-request/',
        },
        DOCVER: {
            LEGACY: '/docs-verification/*',
            ROOT: '/kyc/docs-verification/',
            VERIFICATION: '/kyc/docs-verification/verification/',
            RESULTS: '/kyc/docs-verification/results',
            RESULT: '/kyc/docs-verification/result/',
            CREATE: '/kyc/docs-verification/create',
            PROFILES: '/kyc/docs-verification/verification-profiles',
            PROFILES_CREATE: '/kyc/docs-verification/verification-profiles/create',
            PROFILES_EDIT: '/kyc/docs-verification/verification-profiles/edit/',
        },
        PROFILES: {
            ROOT: '/kyc/profiles/',
            EDIT: '/kyc/profiles/edit/',
            VERIFICATION: '/kyc/profiles/verification-profiles',
            VERIFICATION_CREATE: '/kyc/profiles/verification-profiles/create',
            VERIFICATION_EDIT: '/kyc/profiles/verification-profiles/edit/',
            RISK: '/kyc/profiles/risk-profiles',
            RISK_CREATE: '/kyc/profiles/risk-profiles/create/',
            RISK_EDIT: '/kyc/profiles/risk-profiles/edit/',
            RISK_VIEW: '/kyc/profiles/risk-profiles/view/',
        },
        PEPS: {
            LEGACY: '/pep-sanctions/*',
            ROOT: '/kyc/pep-sanctions/',
            REPORTS: '/kyc/pep-sanctions/reports',
            REPORTS_RESULT: '/kyc/pep-sanctions/reports/search-results/',
            HISTORY: '/kyc/pep-sanctions/request-history/',
            DATA_SOURCES: '/kyc/pep-sanctions/data-sources',
        },
        POA_GEN: {
            ROOT: '/kyc/poa-gen',
            CREATE: '/kyc/poa-gen/create',
            HISTORY: '/kyc/poa-gen/history',
        },
        BILLING: {
            ROOT: '/kyc/billing',
            TOPUP: '/kyc/billing/topup',
            CRYPTO: '/kyc/billing/crypto',
        },
        SETTINGS: {
            ROOT: '/kyc/settings/',
            COMPANY_SETTINGS: '/kyc/settings/company-settings',
            USER_SETTINGS: '/kyc/settings/user-settings',
            ADD_ORGANIZATION: '/kyc/settings/company-settings/add-organization',
            INVITE_TEAM_MEMBERS: '/kyc/settings/company-settings/invite-team-members',
            EDIT_ORGANIZATION: '/kyc/settings/company-settings/edit-organization',
        },
        CONTACT_US: '/kyc/contact-us',
        API: {
            ROOT: '/kyc/api/',
            SETTINGS: '/kyc/api/settings',
        },
        VERIFICATION_FLOW: {
            ROOT: '/verification-flow/',
            CREATE: '/verification-flow/create',
            EDIT: '/verification-flow/edit/:id',
        },
    },

    // KYB (Bolt) routes
    KYB: {
        ROOT: '/kyb/',
        DEFAULT_EMPTY: '/kyb/*',
        ONBOARDING: '/kyb/onboarding',
        DETAILS: '/kyb/onboarding/details/',
        CONTACT_US: '/kyb/contact-us',
        UNDER_DEVELOPMENT: '/kyb/under-development',
        ACCESS_DENIED: '/kyb/access-denied',
    },

    // Deepfake routes
    DEEPFAKE: {
        ROOT: '/deepfake/',
        DEFAULT_EMPTY: '/deepfake/*',
        DASHBOARD: '/deepfake/dashboard',
        VIDEO: '/deepfake/video',
        DOCUMENT: '/deepfake/document',
        AUDIO: '/deepfake/audio',
        IMAGE: '/deepfake/image',
        STREAMING: '/deepfake/streaming',
        DEMO: '/deepfake/demo',
        BILLING: {
            ROOT: '/deepfake/billing',
            TOPUP: '/deepfake/billing/topup',
            CRYPTO: '/deepfake/billing/crypto',
        },
        SETTINGS: {
            ROOT: '/deepfake/settings/',
            COMPANY_SETTINGS: '/deepfake/settings/company-settings',
            USER_SETTINGS: '/deepfake/settings/user-settings',
            ADD_ORGANIZATION: '/deepfake/settings/company-settings/add-organization',
            INVITE_TEAM_MEMBERS: '/deepfake/settings/company-settings/invite-team-members',
            EDIT_ORGANIZATION: '/deepfake/settings/company-settings/edit-organization',
        },
        CONTACT_US: '/deepfake/contact-us',
        API: {
            ROOT: '/deepfake/api/',
            SETTINGS: '/deepfake/api/settings',
        },
    },

    // KYT routes
    KYT: {
        ROOT: '/kyt/',
        DEFAULT_EMPTY: '/kyt/*',
        WALLET_SCORING: '/kyt/wallet-scoring',
        ADDRESS_CHECK: '/kyt/wallet-scoring/type/:type/id/:id',
        BILLING: {
            ROOT: '/kyt/billing',
            TOPUP: '/kyt/billing/topup',
            CRYPTO: '/kyt/billing/crypto',
        },
        SETTINGS: {
            ROOT: '/kyt/settings/',
            COMPANY_SETTINGS: '/kyt/settings/company-settings',
            USER_SETTINGS: '/kyt/settings/user-settings',
            ADD_ORGANIZATION: '/kyt/settings/company-settings/add-organization',
            INVITE_TEAM_MEMBERS: '/kyt/settings/company-settings/invite-team-members',
            EDIT_ORGANIZATION: '/kyt/settings/company-settings/edit-organization',
        },
        CONTACT_US: '/kyt/contact-us',
        API: {
            ROOT: '/kyt/api/',
            SETTINGS: '/kyt/api/settings',
        },
    },

    API: {
        DOCS: '/api/docs',
    },
};
